/**
 * Trang thông báo `/notifications` (plan 2026-10-03 §1): danh sách (tất cả / chưa đọc) +
 * cài đặt cá nhân — múi giờ, giờ yên lặng (không gửi email trong khung này), tắt email
 * theo danh mục. Thông báo trong hệ thống luôn bật.
 */
import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Form,
  List,
  Pagination,
  Result,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  TimePicker,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import {
  type NotificationCategory,
  useMarkAllRead,
  useNotificationPreferences,
  useNotifications,
  useSaveNotificationPreferences,
} from "@/api/notifications";
import { NotificationItem } from "@/components/NotificationBell";
import { useOpenNotification } from "@/components/notificationHelpers";
import { useAuthStore } from "@/stores/auth";

const { Title, Text } = Typography;

const PAGE_SIZE = 20;

// Học viên chủ yếu ở VN / Nhật / Đài / Hàn — đưa lên đầu danh sách.
const COMMON_ZONES = ["Asia/Ho_Chi_Minh", "Asia/Tokyo", "Asia/Seoul", "Asia/Taipei"];

function allZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  const rest = intl.supportedValuesOf?.("timeZone") ?? [];
  return [...COMMON_ZONES, ...rest.filter((z) => !COMMON_ZONES.includes(z))];
}

function PreferencesCard() {
  const prefs = useNotificationPreferences();
  const save = useSaveNotificationPreferences();
  const [form] = Form.useForm();
  const [zones] = useState(allZones);

  useEffect(() => {
    if (!prefs.data) return;
    form.setFieldsValue({
      timezone: prefs.data.timezone,
      quiet: [dayjs(prefs.data.quiet_start, "HH:mm"), dayjs(prefs.data.quiet_end, "HH:mm")],
      email_enabled: prefs.data.categories
        .map((c) => c.value)
        .filter((c) => !prefs.data!.email_disabled_categories.includes(c)),
    });
  }, [prefs.data, form]);

  if (prefs.isLoading) return <Card loading title="Cài đặt thông báo" />;
  if (!prefs.data) return null;

  return (
    <Card title="Cài đặt thông báo">
      <Form
        form={form}
        layout="vertical"
        onFinish={(v: {
          timezone: string;
          quiet: [dayjs.Dayjs, dayjs.Dayjs];
          email_enabled: NotificationCategory[];
        }) =>
          save.mutate(
            {
              timezone: v.timezone,
              quiet_start: v.quiet[0].format("HH:mm"),
              quiet_end: v.quiet[1].format("HH:mm"),
              email_disabled_categories: prefs
                .data!.categories.map((c) => c.value)
                .filter((c) => !v.email_enabled.includes(c)),
            },
            {
              onSuccess: () => message.success("Đã lưu cài đặt"),
              onError: () => message.error("Không lưu được cài đặt"),
            },
          )
        }
      >
        <Form.Item
          label="Múi giờ của bạn"
          name="timezone"
          extra="Giờ hạn nộp trong thông báo và giờ yên lặng tính theo múi giờ này."
        >
          <Select showSearch options={zones.map((z) => ({ value: z, label: z }))} />
        </Form.Item>
        <Form.Item
          label="Giờ yên lặng (không gửi email)"
          name="quiet"
          extra="Email phát sinh trong khung này sẽ gửi khi hết giờ yên lặng."
        >
          <TimePicker.RangePicker format="HH:mm" order={false} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item label="Nhận email cho các mục" name="email_enabled">
          <Checkbox.Group
            options={prefs.data.categories.map((c) => ({ value: c.value, label: c.label }))}
            style={{ display: "flex", flexDirection: "column", gap: 6 }}
          />
        </Form.Item>
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          Thông báo trong hệ thống (biểu tượng chuông) luôn được bật.
        </Text>
        <Button type="primary" htmlType="submit" loading={save.isPending}>
          Lưu cài đặt
        </Button>
      </Form>
    </Card>
  );
}

export function Notifications() {
  const user = useAuthStore((s) => s.user);
  const enabled = !!user?.feature_flags?.notifications;
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [page, setPage] = useState(1);
  const list = useNotifications(
    { unread: filter === "unread", limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE },
    enabled,
  );
  const markAll = useMarkAllRead();
  const openItem = useOpenNotification();

  if (!enabled) {
    return <Result status="info" title="Tính năng thông báo chưa được bật" />;
  }
  if (!user?.tenant) {
    return (
      <Alert type="info" showIcon message="Tài khoản quản trị hệ thống không nhận thông báo." />
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <Title level={3}>Thông báo</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={15}>
          <Card
            title={
              <Segmented
                value={filter}
                onChange={(v) => {
                  setFilter(v as "all" | "unread");
                  setPage(1);
                }}
                options={[
                  { label: "Tất cả", value: "all" },
                  { label: `Chưa đọc (${list.data?.unread ?? 0})`, value: "unread" },
                ]}
              />
            }
            extra={
              <Button
                disabled={!list.data?.unread}
                loading={markAll.isPending}
                onClick={() => markAll.mutate()}
              >
                Đánh dấu đã đọc hết
              </Button>
            }
          >
            {list.isLoading ? (
              <div style={{ textAlign: "center", padding: 32 }}>
                <Spin />
              </div>
            ) : list.data?.results.length ? (
              <Space direction="vertical" style={{ width: "100%" }}>
                <List
                  dataSource={list.data.results}
                  split={false}
                  renderItem={(item) => <NotificationItem item={item} onOpen={openItem} />}
                />
                {list.data.count > PAGE_SIZE && (
                  <Pagination
                    current={page}
                    pageSize={PAGE_SIZE}
                    total={list.data.count}
                    onChange={setPage}
                    style={{ textAlign: "center" }}
                  />
                )}
              </Space>
            ) : (
              <Empty description="Không có thông báo" />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={9}>
          <PreferencesCard />
        </Col>
      </Row>
    </div>
  );
}
